import React, { ReactNode } from 'react';
import styled from 'styled-components';

import DataPair from '#/components/basic/panels/DataPair';
import { filterEmpty } from '#/lib/utils';

import BasicTable, {
  Column,
  RowBackground,
  type HeaderColumn,
} from './BasicTable';

const MobileDataPairRow = styled.div<{ $selected: boolean }>`
  width: 100%;
  border-radius: 5px;
  border: 1px solid
    ${({ $selected }) => ($selected ? 'var(--yellow5)' : 'var(--separator)')};
  padding: 8px;
  margin: 8px 0;
`;

type ColumnWithRender<T> = HeaderColumn & {
  /** ReactNode or false if columns should be hidden */
  render: ((item: T) => ReactNode) | ReactNode | false;
  stackWithPrevious?: boolean;
  textWrap?: boolean;
  /** Default is digits font */
  fontFamily?: 'standard' | 'digits';
  allowTruncate?: boolean;
  onValueClick?: () => void;
};

/** Updated BasicTable properties to better match stacking requirements */
type ITableProps<T> = {
  loading?: boolean;

  items: T[];

  columns: (item?: T) => ColumnWithRender<T>[];

  rowCustomizer?: (item: T) => {
    selected?: boolean;
    underlined?: boolean;
    background?: RowBackground;
    fontSize?: string;
  };
  getItemKey: (item: T, index: number) => string;

  rowClickable?: (item: T) => boolean;
  onRowClick?: (item: T) => void;

  appearAnimationColor?: (item: T) => 'green' | 'red';
  watchValueAndAnimateOnChange?: (item: T) => string;
  firstColumnPaddingLeft?: string;
  errorText?: ReactNode;
  rowsVerticalPadding?: number;
  /** Min height of body rows in px. Content can expand the row if it is taller. */
  rowMinHeight?: number;

  /* Note: this will partially ignore fixed widths of columns */
  notFixedLayout?: boolean;
  noHoverEffect?: boolean;
  stripes?: boolean;
  className?: string;
  marginTop?: string;
  marginBottom?: string;

  /** Experimental: Special DataPaird which will ignore some normal table properties */
  specificRenderAsDataPairs?: boolean;

  /** @deprecated */
  headerBackground?: 'dark' | 'gray';
};

function renderColumn<T>(item: T, column: ColumnWithRender<T>) {
  return (
    <>
      {typeof column.render === 'function' ? (
        column.render(item)
      ) : column.render ? (
        <>{column.render}</>
      ) : null}
    </>
  );
}

function BasicTableStackable<T>({
  loading,
  columns: columnsFn,
  items,
  getItemKey,
  notFixedLayout,
  noHoverEffect,
  rowCustomizer,
  rowClickable,
  onRowClick,
  appearAnimationColor,
  watchValueAndAnimateOnChange,
  firstColumnPaddingLeft,
  errorText,
  rowsVerticalPadding,
  rowMinHeight,
  stripes,
  marginTop,
  marginBottom,
  className,
  specificRenderAsDataPairs,
  headerBackground,
}: ITableProps<T>) {
  const visibleColumns = columnsFn(undefined).filter(
    column => column.render !== false
  );
  if (specificRenderAsDataPairs) {
    // This is experimental rendering method
    return (
      <>
        {items.map((item, index) => {
          const itemColumns = columnsFn(item).filter(
            column => column.render !== false
          );
          return (
            <MobileDataPairRow
              key={getItemKey(item, index)}
              $selected={!!rowCustomizer && !!rowCustomizer(item).selected}
            >
              {itemColumns.map((column, index) => {
                return (
                  <DataPair
                    key={column.name}
                    label={column.name}
                    altBackground={index % 2 !== 0}
                    childrenFontFamily={column.fontFamily}
                  >
                    {renderColumn(item, column)}
                  </DataPair>
                );
              })}
            </MobileDataPairRow>
          );
        })}
      </>
    );
  }
  return (
    <BasicTable
      loading={loading}
      columns={visibleColumns
        .filter(filterEmpty)
        .map((column, index) => {
          const nextColumn = visibleColumns[index + 1];
          return nextColumn?.stackWithPrevious
            ? { ...column, nameStacked: nextColumn.name }
            : column;
        })
        .filter(column => !column.stackWithPrevious)}
      items={items}
      getItemKey={getItemKey}
      notFixedLayout={notFixedLayout}
      noHoverEffect={noHoverEffect}
      rowCustomizer={rowCustomizer}
      rowClickable={rowClickable}
      onRowClick={onRowClick}
      appearAnimationColor={appearAnimationColor}
      watchValueAndAnimateOnChange={watchValueAndAnimateOnChange}
      firstColumnPaddingLeft={firstColumnPaddingLeft}
      errorText={errorText}
      rowsVerticalPadding={rowsVerticalPadding}
      rowMinHeight={rowMinHeight}
      stripes={stripes}
      className={className}
      marginTop={marginTop}
      marginBottom={marginBottom}
      headerBackground={headerBackground}
      renderRow={item => {
        const itemColumns = columnsFn(item).filter(
          column => column.render !== false
        );
        return (
          <>
            {itemColumns
              .map((column, columnIndex) => {
                if (column.stackWithPrevious) {
                  return null;
                }
                const nextColumn = itemColumns[columnIndex + 1];
                if (nextColumn?.stackWithPrevious) {
                  return (
                    <Column
                      key={column.name + columnIndex}
                      center={column.align === 'center'}
                      left={column.align === 'left'}
                      right={column.align === 'right'}
                      textWrap={column.textWrap}
                      fontFamily={column.fontFamily}
                      onClick={column.onValueClick}
                      /* Default font as most tables display numbers */
                      // font-family: var(--digitsFontFamily);
                    >
                      {renderColumn(item, column)}
                      {nextColumn && (
                        <>
                          <br />{' '}
                          {nextColumn.fontFamily !== column.fontFamily ? (
                            <div
                              style={{
                                display: 'inline',
                                fontFamily:
                                  nextColumn.fontFamily === 'standard'
                                    ? 'var(--standardFontFamily)'
                                    : 'var(--digitsFontFamily)',
                              }}
                            >
                              {renderColumn(item, nextColumn)}
                            </div>
                          ) : (
                            renderColumn(item, nextColumn)
                          )}
                        </>
                      )}
                    </Column>
                  );
                }
                return (
                  <Column
                    key={column.name + columnIndex}
                    center={column.align === 'center'}
                    left={column.align === 'left'}
                    right={column.align === 'right'}
                    textWrap={column.textWrap}
                    fontFamily={column.fontFamily}
                    onClick={column.onValueClick}
                  >
                    {renderColumn(item, column)}
                  </Column>
                );
              })
              .filter(column => column !== null)}
          </>
        );
      }}
    />
  );
}

export default BasicTableStackable;
